'use client';

import { datadogLogs } from '@datadog/browser-logs';
import type { LogRecord } from '@logtape/logtape';

import { EnvironmentNameEnum, getEnvironmentName } from '@/config/environment';
import { redactAttributes, redactText } from '@/logger/redaction/redact';

let initialized = false;
const DATADOG_TOKEN = 'puba1ad5273bdb658063531500b18b763f8';

const DATADOG_LEVELS: Record<string, 'debug' | 'info' | 'warn' | 'error'> = {
  trace: 'debug',
  debug: 'debug',
  info: 'info',
  warning: 'warn',
  error: 'error',
  fatal: 'error',
};

function initDatadog() {
  if (initialized || typeof window === 'undefined') {
    return;
  }

  const environmentName = getEnvironmentName();

  if (environmentName === EnvironmentNameEnum.LOCAL) {
    return;
  }

  initialized = true;

  datadogLogs.init({
    clientToken: DATADOG_TOKEN,
    site: 'datadoghq.com',
    service: 'medprev-web-app-frontend',
    env: environmentName,
    version: process.env.NEXT_PUBLIC_GIT_COMMIT_SHA,
    sessionSampleRate: 100,
    forwardErrorsToLogs: true,
    beforeSend: (log) => {
      const event = log as unknown as Record<string, unknown>;

      for (const [key, value] of Object.entries(redactAttributes(event))) {
        event[key] = value;
      }

      return true;
    },
  });
}

export function getDatadogClientSink() {
  if (typeof window !== 'undefined') {
    setTimeout(initDatadog, 0);
  }

  return (record: LogRecord) => {
    if (!initialized) {
      return;
    }

    const level = DATADOG_LEVELS[record.level] ?? 'info';
    const [, ...emittingModule] = record.category;
    const context = emittingModule.join('.');

    datadogLogs.logger[level]?.(
      redactText(record.message.join('')),
      redactAttributes(context ? { ...record.properties, context } : record.properties),
    );
  };
}
